// Mirrors the Go backend's application/consignment_dto.go and the Postgres
// consignment_status enum (migration 000017_create_consignment_tables).
export type ConsignmentStatus = 'active' | 'sold' | 'paid_out' | 'withdrawn'

export interface ConsignorBrief {
  id: string
  first_name: string
  last_name: string
}

export interface ConsignmentBoatBrief {
  id: string
  make: string
  model: string
  year: number
  price: number
}

export interface Consignment {
  id: string
  boat_id: string
  consignor_id: string
  status: ConsignmentStatus
  commission_rate: number // percent, e.g. 10 = 10%
  asking_price: number
  sale_price?: number | null // set once the sale is recorded
  deductions?: number | null // expenses netted from owner proceeds
  commission_amount?: number | null // snapshot at sale time
  net_proceeds?: number | null // snapshot: sale - commission - deductions
  sold_at?: string | null
  paid_out_at?: string | null
  agreement_start: string
  agreement_end?: string | null
  notes: string
  consignor?: ConsignorBrief | null
  boat?: ConsignmentBoatBrief | null
  created_at: string
  updated_at: string
}

// Body for POST /inventory/consignments.
export interface ConsignmentWriteBody {
  id?: string // client-generated for offline create replay idempotency
  boat_id: string
  consignor_id: string
  commission_rate: number
  asking_price: number
  agreement_end?: string
  notes?: string
}

// Body for PUT /inventory/consignments/{id} — terms only; sale and payout
// fields are set through their dedicated actions.
export interface ConsignmentTermsBody {
  commission_rate: number
  asking_price: number
  agreement_end?: string
  notes?: string
}

// Body for POST /inventory/consignments/{id}/sale.
export interface RecordSaleBody {
  sale_price: number
  sold_at?: string
  deductions?: number
}

export const CONSIGNMENT_STATUSES: ConsignmentStatus[] = ['active', 'sold', 'paid_out', 'withdrawn']

export const CONSIGNMENT_STATUS_LABELS: Record<ConsignmentStatus, string> = {
  active: 'Active',
  sold: 'Sold',
  paid_out: 'Paid Out',
  withdrawn: 'Withdrawn',
}

// Badge variant per status (shadcn badge + Tailwind tone classes).
export const CONSIGNMENT_STATUS_VARIANT: Record<ConsignmentStatus, string> = {
  active: 'bg-info/15 text-info border-info/30',
  sold: 'bg-warning/15 text-warning border-warning/30',
  paid_out: 'bg-success/15 text-success border-success/30',
  withdrawn: 'bg-muted text-muted-foreground border-border',
}

export const consignorName = (c: Consignment): string =>
  c.consignor ? `${c.consignor.first_name} ${c.consignor.last_name}`.trim() : '—'

export const boatName = (c: Consignment): string =>
  c.boat ? `${c.boat.make} ${c.boat.model}`.trim() : '—'
